import { useQuery } from '@tanstack/react-query';
import type { LineDraft, TemplateTarget } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { money } from '../money';
import { Field, field } from './Field';

export interface TemplateFill {
  title: string;
  description: string;
  taxPct: string;
  lines: LineDraft[];
}

/**
 * "Start from a template" — the whole point of having saved one. Picking fills the form
 * and then gets out of the way: what lands in the fields is an ordinary draft that can be
 * edited line by line, not a linked object. A template is a starting point, never a
 * binding, which is also why the server copies its lines rather than referencing them.
 */
/**
 * A template's saved lines as rows to edit. Each keeps the price-book item it was saved with,
 * so a quote started from a template can be matched to the parts used on the job (Inventory
 * v2): dropping it here made every templated part read "beyond the quote".
 */
export function templateLines(
  lines: {
    priceBookItemId: string | null;
    name: string;
    qty: string;
    unitPriceCents: number;
    kind: string;
  }[],
): LineDraft[] {
  return lines.map((l) => ({
    ...(l.priceBookItemId ? { priceBookItemId: l.priceBookItemId } : {}),
    name: l.name,
    qty: String(Number(l.qty)),
    price: (l.unitPriceCents / 100).toFixed(2),
    kind: l.kind as LineDraft['kind'],
  }));
}

export function TemplatePicker({
  appliesTo,
  onPick,
}: {
  appliesTo: Exclude<TemplateTarget, 'both'>;
  onPick: (fill: TemplateFill) => void;
}) {
  const trpc = useTRPC();
  const templates = useQuery(trpc.template.list.queryOptions());

  const usable = (templates.data ?? []).filter(
    (t) => t.appliesTo === 'both' || t.appliesTo === appliesTo,
  );

  // Nothing saved yet — say nothing rather than showing an empty control that looks broken.
  if (usable.length === 0) return null;

  return (
    <Field label="Start from a template">
      <select
        className={field}
        defaultValue=""
        onChange={(e) => {
          const tpl = usable.find((t) => t.id === e.target.value);
          if (!tpl) return;
          onPick({
            title: tpl.title,
            description: tpl.description ?? '',
            taxPct: tpl.taxRateBps ? (tpl.taxRateBps / 100).toString() : '',
            lines: templateLines(tpl.lines),
          });
          e.target.value = '';
        }}
      >
        <option value="">Blank — start from scratch</option>
        {usable.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
            {t.lines.length
              ? ` — ${money(t.lines.reduce((s, l) => s + l.totalCents, 0))}`
              : ''}
          </option>
        ))}
      </select>
    </Field>
  );
}
